import type {
  DataQuality,
  DecisionPlan,
  LocationNode,
  PlacementAction,
  ProductAnalysis,
  ProductSeriesPoint,
  ReplenishmentAction,
  SalesRecord,
  VelocityClass,
  WarehouseDataset,
} from "../types";
import {
  addCalendarDays,
  buildDemandForecast,
  cumulativeForecast,
  daysOfStock,
  latestFiniteValue,
} from "./forecast";
import { createWarehouseMap, normalizeLocationCode } from "./warehouse";

function daysBetween(from: string, to: string) {
  const a = new Date(`${from}T12:00:00+08:00`).getTime();
  const b = new Date(`${to}T12:00:00+08:00`).getTime();
  return Math.max(0, Math.ceil((b - a) / 86_400_000));
}

function mean(values: number[]) {
  return values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;
}

function stdDev(values: number[]) {
  if (!values.length) return 0;
  const avg = mean(values);
  return Math.sqrt(mean(values.map((value) => (value - avg) ** 2)));
}

function median(values: number[]) {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

function linearRegression(values: number[]) {
  if (values.length < 2) return { slope: 0, intercept: values[0] ?? 0, r2: 0 };
  const xMean = (values.length - 1) / 2;
  const yMean = mean(values);
  let numerator = 0;
  let denominator = 0;
  values.forEach((value, index) => {
    numerator += (index - xMean) * (value - yMean);
    denominator += (index - xMean) ** 2;
  });
  const slope = denominator ? numerator / denominator : 0;
  const intercept = yMean - slope * xMean;
  const total = values.reduce((sum, value) => sum + (value - yMean) ** 2, 0);
  const residual = values.reduce(
    (sum, value, index) => sum + (value - (intercept + slope * index)) ** 2,
    0,
  );
  return { slope, intercept, r2: total ? Math.max(0, 1 - residual / total) : 0 };
}

export function assessQuality(dataset: WarehouseDataset): DataQuality {
  const dates = [...new Set(dataset.sales.map((sale) => sale.date).filter(Boolean))].sort();
  const skus = new Set(dataset.sales.map((sale) => sale.sku).filter(Boolean));
  const locations = new Set(
    dataset.sales
      .map((sale) => normalizeLocationCode(sale.location ?? ""))
      .filter(Boolean),
  );
  const requiredFields = dataset.sales.length * 4;
  const present = dataset.sales.reduce(
    (sum, row) =>
      sum
      + Number(Boolean(row.date))
      + Number(Boolean(row.sku))
      + Number(Boolean(row.quantity))
      + Number(Boolean(row.product)),
    0,
  );
  const completeness = requiredFields ? present / requiredFields : 0;
  const days = dates.length;
  const level = days >= 21 && completeness >= 0.85 ? "good" : days >= 7 ? "limited" : "insufficient";
  const notes: string[] = [];
  if (!dataset.sales.length) notes.push("当前没有业务数据，等待后端接口或用户导入 CSV / JSON 后再生成决策。");
  if (days < 21) notes.push(`当前覆盖 ${days} 天，少于 21 天；趋势可看，补货区间需谨慎使用。`);
  if (completeness < 0.85) notes.push(`关键字段完整度 ${(completeness * 100).toFixed(1)}%，缺失关键字段的记录已跳过。`);
  if (!locations.size) notes.push("未识别到有效库位编码，不会生成跨库区搬迁建议。");
  if (dataset.sales.some((row) => row.expiresOn)) notes.push("检测到效期字段，批次分配按 ¼ / ⅓ / ½ 临期档位匹配客户要求，并在批次内执行 FEFO。");
  else notes.push("未识别到效期字段，批次效期页不会自动生成到期结论。");
  if (!dataset.sales.some((row) => row.currentStock != null || row.incomingStock != null)) {
    notes.push("未提供现有库存或在途库存；补货量按需求区间给出，执行前应回填实时库存。");
  }
  return {
    days,
    rows: dataset.sales.length,
    products: skus.size,
    locations: locations.size,
    completeness,
    level,
    title:
      level === "good"
        ? "数据可用于滚动决策"
        : level === "limited"
          ? "数据可做有限判断"
          : "数据不足，结论应保守",
    notes,
  };
}

function classify(
  rank: number,
  total: number,
  recentHeat: number,
  heatMedian: number,
  slopePercent: number,
): VelocityClass {
  if (recentHeat <= 0) return "平滞";
  const ratio = total ? rank / total : 1;
  if (ratio <= 0.16 && (slopePercent > -12 || recentHeat >= heatMedian * 1.35)) return "爆旺";
  if (ratio <= 0.62 || recentHeat >= heatMedian * 0.82) return "平销";
  return "平滞";
}

export function analyseDataset(dataset: WarehouseDataset, locations: LocationNode[]): DecisionPlan {
  const quality = assessQuality(dataset);
  const groups = new Map<string, SalesRecord[]>();
  dataset.sales.forEach((record) => {
    const list = groups.get(record.sku) ?? [];
    list.push(record);
    groups.set(record.sku, list);
  });

  const raw = [...groups.entries()].map(([sku, records]) => {
    const forecast = buildDemandForecast(records, dataset.meta.asOf);
    const quantities = forecast.quantities;
    const latest = [...records]
      .filter((record) => record.date <= dataset.meta.asOf)
      .sort((a, b) => b.date.localeCompare(a.date) || b.id.localeCompare(a.id))
      .at(0) ?? records.at(-1)!;
    const location = normalizeLocationCode(latest.location ?? "") || latest.location || "UNASSIGNED";
    return {
      sku,
      product: latest.product || sku,
      location,
      quantity: quantities.reduce((sum, value) => sum + value, 0),
      orders: records.reduce((sum, row) => sum + (row.orders ?? 1), 0),
      averageDaily: mean(quantities),
      avg3d: forecast.avg3d,
      avg7d: forecast.avg7d,
      recentHeat: forecast.recentHeat,
      slope: forecast.regression.slope,
      slopePercent: forecast.regression.slopePercent,
      r2: forecast.regression.r2,
      volatility: mean(quantities) ? stdDev(quantities) / mean(quantities) : 0,
      currentStock: latestFiniteValue(records, "currentStock", dataset.meta.asOf),
      incomingStock: latestFiniteValue(records, "incomingStock", dataset.meta.asOf),
      forecast,
    };
  });
  const forecastBySku = new Map(raw.map((item) => [item.sku, item.forecast]));

  const heatMedian = median(raw.map((item) => item.recentHeat).filter((value) => value > 0));
  const sorted = [...raw].sort((a, b) => b.recentHeat - a.recentHeat);
  const products: ProductAnalysis[] = sorted.map((item, rank) => {
    const category = classify(rank, sorted.length, item.recentHeat, heatMedian, item.slopePercent);
    const recommendedCycle = category === "爆旺" ? 1 : category === "平销" ? 7 : 30;
    const demand = cumulativeForecast(item.forecast, recommendedCycle);
    const availableStock = item.currentStock + item.incomingStock;
    const suggestedQuantity = Math.max(0, Math.ceil(demand.upper - availableStock));
    return {
      sku: item.sku,
      product: item.product,
      location: item.location,
      category,
      quantity: item.quantity,
      orders: item.orders,
      averageDaily: item.averageDaily,
      avg3d: item.avg3d,
      avg7d: item.avg7d,
      recentHeat: item.recentHeat,
      slope: item.slope,
      slopePercent: item.slopePercent,
      r2: item.r2,
      volatility: item.volatility,
      recommendedCycle,
      forecastDaily: item.forecast.forecastDaily,
      forecastLowerDaily: item.forecast.forecastLowerDaily,
      forecastUpperDaily: item.forecast.forecastUpperDaily,
      suggestedQuantity,
      currentStock: item.currentStock,
      incomingStock: item.incomingStock,
      stockDays: daysOfStock(item.forecast, availableStock),
      confidence: item.forecast.confidence,
      series: item.forecast.series,
      forecastSeries: item.forecast.future,
      periodDays: item.forecast.periodDays,
      periodConfidence: item.forecast.periodConfidence,
      shockDays: item.forecast.shockDays,
      riskNote: item.forecast.riskNote,
    };
  });

  const locMap = new Map(locations.map((location) => [location.code, location]));
  const reservedTargets = new Map<string, Set<string>>();
  const placements: PlacementAction[] = [];
  const warningRisks: string[] = [];
  const hotProducts = products
    .filter((product) => product.category === "爆旺")
    .sort((a, b) => b.recentHeat - a.recentHeat);

  hotProducts.forEach((product) => {
    const current = locMap.get(product.location);
    if (!current) {
      warningRisks.push(`${product.product} 的当前库位无法匹配实体库位，已跳过库区外重排。`);
      return;
    }
    const reserved = reservedTargets.get(current.zone) ?? new Set<string>();
    reservedTargets.set(current.zone, reserved);
    const currentRoute = current.routeMeters || current.distance;
    const minimumGain = Math.max(2.5, currentRoute * 0.08);
    const target = locations
      .filter(
        (location) =>
          location.zone === current.zone
          && location.code !== current.code
          && location.encoded
          && location.zone !== "SPECIAL"
          && !reserved.has(location.code)
          && (location.routeMeters || location.distance) < currentRoute - minimumGain,
      )
      .sort(
        (a, b) =>
          (a.routeMeters || a.distance) - (b.routeMeters || b.distance)
          || a.code.localeCompare(b.code),
      )[0];
    if (!target) return;
    reserved.add(target.code);
    const targetRoute = target.routeMeters || target.distance;
    placements.push({
      sku: product.sku,
      product: product.product,
      from: current.code,
      to: target.code,
      category: product.category,
      fromDistance: currentRoute,
      toDistance: targetRoute,
      reason:
        `近 7 日出库均量 ${product.avg7d.toFixed(1)} 件、近 3 日 ${product.avg3d.toFixed(1)} 件；`
        + `仅在 ${current.zone} 库区内向出库口移动，路线由 ${currentRoute.toFixed(0)} 米缩短到 ${targetRoute.toFixed(0)} 米。`,
      priority: 1,
    });
  });

  const replenishments: ReplenishmentAction[] = products.map((product) => {
    const leadDays = 3;
    const orderInDays = product.stockDays < 998
      ? Math.max(0, Math.floor(product.stockDays - leadDays))
      : product.recommendedCycle;
    const cycleDemand = cumulativeForecast(
      forecastBySku.get(product.sku) ?? buildDemandForecast([], dataset.meta.asOf),
      product.recommendedCycle,
    );
    return {
      sku: product.sku,
      product: product.product,
      category: product.category,
      cycle: product.recommendedCycle,
      forecastDaily: product.forecastDaily,
      forecastLowerDaily: product.forecastLowerDaily,
      forecastUpperDaily: product.forecastUpperDaily,
      suggestedQuantity: product.suggestedQuantity,
      stockDays: product.stockDays,
      nextOrderDate: addCalendarDays(dataset.meta.asOf, orderInDays),
      confidence: product.confidence,
      periodDays: product.periodDays,
      periodConfidence: product.periodConfidence,
      shockDays: product.shockDays,
      reason:
        `近 7 日出库均量 ${product.avg7d.toFixed(1)} 件；阻尼 Holt 预测覆盖 ${product.recommendedCycle} 日需求 `
        + `${cycleDemand.mean.toFixed(0)} 件，90% 需求区间 ${cycleDemand.lower.toFixed(0)}–${cycleDemand.upper.toFixed(0)} 件；`
        + `建议量取区间上界并扣除现有及在途库存。${product.riskNote}`,
    };
  });

  const hotCount = products.filter((item) => item.category === "爆旺").length;
  const stableCount = products.filter((item) => item.category === "平销").length;
  const slowCount = products.filter((item) => item.category === "平滞").length;
  const urgent = replenishments.filter((item) => item.stockDays <= item.cycle).length;
  const risks = [
    ...(quality.level === "good" ? [] : [quality.notes[0] ?? "预测数据不足，请保守执行。"]),
    ...warningRisks,
    ...(dataset.batches.some((batch) => daysBetween(dataset.meta.asOf, batch.expiresOn) <= 14)
      ? ["存在 14 天内到期批次，应优先匹配短周转客户并复核批次效期。"]
      : []),
    ...(dataset.batches.some((batch) => batch.recallFlag)
      ? ["存在召回标记批次，任何自动分配都应先冻结并人工复核。"]
      : []),
  ];

  return {
    generatedAt: new Date().toISOString(),
    quality,
    products,
    placements: placements.slice(0, 24),
    replenishments,
    headline: !dataset.sales.length
      ? "等待业务数据接入"
      : hotCount
        ? `${hotCount} 个近期高频品可优化同库区拣货路线`
        : "近期未形成明确高频品",
    summary: dataset.sales.length
      ? [
          `以近 3 日与近 7 日出库量识别出 ${hotCount} 个爆旺、${stableCount} 个平销、${slowCount} 个平滞品项。`,
          `预计 ${urgent} 个品项在本补货周期内需要关注；搬迁建议严格限制在原库区内部。`,
          "补货量由线性趋势初始化、阻尼 Holt 预测、异常脉冲识别和 2–14 日周期检测共同生成，并按渐宽预测区间计算。",
        ]
      : [
          "当前业务数据已清空，空间库位目录仍然保留。",
          "请导入 CSV / JSON，或接入后端接口后刷新数据。",
          "在数据到达前，不生成商品趋势、批次分配或补货结论。",
        ],
    risks,
    hotCount,
    stableCount,
    slowCount,
  };
}

export function productRegression(series: ProductSeriesPoint[]) {
  const values = series.map((point) => point.quantity);
  const model = linearRegression(values);
  return {
    ...model,
    points: series.map((point, index) => ({
      ...point,
      x: index,
      fitted: point.fitted ?? model.intercept + model.slope * index,
    })),
  };
}

export function makeLocationPlan(dataset: WarehouseDataset) {
  return createWarehouseMap(dataset);
}
